import { BarChart3, Heart, Send, Users, Video } from 'lucide-react'
import { useMemo } from 'react'

import { useExternalUploads } from '@/api/endpoints/analytics'
import { useTiktokProfile, useTiktokUploadStatus } from '@/api/endpoints/tiktok/uploads'
import type { Channel } from '@/api/endpoints/channels'
import { EmptyState } from '@/components/ds/EmptyState'
import { PageHeader } from '@/components/ds/PageHeader'
import { Section } from '@/components/ds/Section'
import { Chip } from '@/components/ui/Chip'
import { KPIGrid } from '@/features/analytics/shared/KPIGrid'
import { UploadsTable, UploadsTableSkeleton, type UploadRow } from '@/features/analytics/shared/UploadsTable'
import styles from '@/features/analytics/pages/AnalyticsPage/AnalyticsPage.module.scss'

interface TiktokAnalyticsPageProps {
  channel: Channel
}

/**
 * TikTok analytics — profile counters via Login Kit + local upload
 * tracker. Doesn't yet pull per-video counters because we dropped the
 * ``video.list`` scope to fast-track app review (see Phase 3 of the
 * integration plan); when that scope comes back through a follow-up
 * review, this page sprouts a "Vídeos publicados" tab equivalent to the
 * YouTube one.
 */
export function TiktokAnalyticsPage({ channel }: TiktokAnalyticsPageProps) {
  const profile = useTiktokProfile(channel.slug)
  const status = useTiktokUploadStatus(channel.slug)
  const externalUploads = useExternalUploads(channel.slug)

  const uploads = status.data?.uploads ?? []
  const published = uploads.filter((u) => u.status === 'published')
  const pending = uploads.filter((u) => u.status === 'pending' || u.status === 'uploading')
  const failed = uploads.filter((u) => u.status === 'failed')

  const profileData = profile.data
  const isConnected = profileData?.connected === true

  // Build the top-uploads table rows from the external_stats endpoint.
  // Every video on the user's TikTok profile is returned by /v2/video/list/
  // (sub-phase 2 cron): videos linked to a Prism upload land in
  // tiktok_video_stats; the rest in tiktok_external_stats. The endpoint
  // returns the latter, which for a sandbox tester usually IS every
  // public video on the profile.
  const externalRows: UploadRow[] = useMemo(
    () =>
      (externalUploads.data?.items ?? []).map((e) => ({
        videoId: '',
        language: '',
        externalId: e.external_id,
        title: e.title || e.external_id,
        views: e.view_count,
        likes: e.like_count,
        comments: e.comment_count,
        isExternal: true,
        externalUrl: e.share_url || `https://www.tiktok.com/embed/${e.external_id}`,
        thumbnailUrl: e.thumbnail_url,
      })),
    [externalUploads.data],
  )

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Visão do perfil"
        title="Analytics"
        description={`Snapshot do TikTok ${channel.name}. Métricas por vídeo chegam quando o app passar pelo review e re-habilitarmos o scope video.list.`}
      />

      <KPIGrid
        loading={profile.isPending}
        error={profile.error}
        onRetry={() => {
          void profile.refetch()
        }}
        items={[
          {
            icon: <Users />,
            label: 'Seguidores',
            value: profileData?.follower_count,
            hint: 'TikTok · snapshot atual',
          },
          {
            icon: <Video />,
            label: 'Postagens',
            value: profileData?.video_count,
            hint: 'total publicado no perfil TikTok',
          },
          {
            icon: <Heart />,
            label: 'Curtidas',
            value: profileData?.likes_count,
            hint: 'soma em todos os vídeos do perfil',
          },
          {
            icon: <Send />,
            label: 'Publicados pelo Prism',
            value: published.length,
            hint: `${String(uploads.length)} uploads no total`,
          },
        ]}
      />

      <Section
        title="Top uploads no perfil"
        description={
          externalUploads.isPending
            ? 'Carregando...'
            : !isConnected
              ? 'Perfil não conectado'
              : `${String(externalRows.length)} vídeo${externalRows.length === 1 ? '' : 's'} público${externalRows.length === 1 ? '' : 's'} no perfil TikTok`
        }
      >
        {!isConnected ? (
          <EmptyState
            title="Perfil TikTok não conectado"
            description="Conecte o perfil em /channels pra ver os vídeos do perfil aqui."
          />
        ) : externalUploads.isPending ? (
          <UploadsTableSkeleton />
        ) : externalRows.length === 0 ? (
          <EmptyState
            icon={<BarChart3 />}
            title="Nenhum vídeo público encontrado"
            description="O cron horário descobre os vídeos do perfil via /v2/video/list/. Aguarde o próximo tick ou clique em Atualizar quando houver."
          />
        ) : (
          <UploadsTable rows={externalRows} statsLoading={false} />
        )}
      </Section>

      <Section
        title="Uploads via Prism"
        description={
          status.isPending
            ? 'Carregando...'
            : !isConnected
              ? 'Perfil não conectado'
              : `${String(uploads.length)} upload${uploads.length === 1 ? '' : 's'} registrados`
        }
      >
        {!isConnected ? (
          <EmptyState
            title="Perfil TikTok não conectado"
            description="Conecte o perfil em /channels pra começar a publicar e ver os uploads aqui."
          />
        ) : uploads.length === 0 ? (
          <EmptyState
            title="Nenhum upload ainda"
            description="Publique um vídeo via Prism (Project Detail → Publicar → TikTok) e ele aparece aqui."
          />
        ) : (
          <ul className={styles.uploadsList}>
            {uploads.map((u) => (
              <li key={u.id} className={styles.uploadRow}>
                <div className={styles.uploadRowMain}>
                  <span className={styles.uploadTitle}>{u.title || u.video_id}</span>
                  <span className={styles.uploadMeta}>
                    {u.language} · {formatDate(u.uploaded_at ?? u.created_at)}
                  </span>
                </div>
                <UploadStatusChip status={u.status} />
              </li>
            ))}
          </ul>
        )}

        {pending.length > 0 ? (
          <p className={styles.uploadsNote}>
            {pending.length} upload{pending.length === 1 ? '' : 's'} em andamento.
          </p>
        ) : null}

        {failed.length > 0 ? (
          <p className={styles.uploadsNote}>
            {failed.length} upload{failed.length === 1 ? ' falhou' : 's falharam'}. Confira os
            jobs em <a href="/jobs">/jobs</a>.
          </p>
        ) : null}
      </Section>
    </div>
  )
}

function UploadStatusChip({ status }: { status: string }) {
  if (status === 'published') return <Chip variant="success">Publicado</Chip>
  if (status === 'failed') return <Chip variant="danger">Falhou</Chip>
  if (status === 'uploading') return <Chip variant="warning">Enviando</Chip>
  if (status === 'pending') return <Chip>Pendente</Chip>
  return <Chip>{status}</Chip>
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return iso
  }
}
